/**
 * The rules behind the lifecycle panel on a contact page.
 *
 * Named for the concern rather than the component, like touchLog.ts beside
 * TouchHistory.tsx. It was briefly `lifecyclePanel.ts`, which differs from
 * `LifecyclePanel.tsx` only in case -- one file on macOS, two on Linux -- and
 * tsc refused the program outright (TS1149) before anything could run. The
 * rule is not a style preference; it does not compile.
 *
 * WHY THIS EXISTS AT ALL
 * ----------------------
 * The database already models a lead's lifecycle -- a stage, a cadence, a
 * dated snooze -- and `contact_health` already computes who is overdue. None
 * of it was reachable from the app. A stage nobody can set is a column, not a
 * process, and a record sitting still looked exactly like a record being
 * worked, which is the Zoho failure this system was built to end.
 *
 * WHY THE RULES ARE HERE AND NOT IN THE COMPONENT
 * -----------------------------------------------
 * The `marketing` vitest project globs plain `.ts` and runs in Node; `app`
 * runs in real Chromium and costs a ~130 MB Playwright download. Every rule
 * below can silently corrupt what a person reads or what gets written to the
 * lifecycle table, and none of them needs a DOM -- so they should not sit one
 * browser install away from being skipped.
 *
 * THE TRAP THIS FILE EXISTS TO CLOSE
 * ----------------------------------
 * `lifecycle_stages` is keyed on (stage, track), not on stage. `new` and
 * `contacted` exist in BOTH tracks with different cadences -- 7 days for a
 * prospect, 30 for a partner. `contact_health` joins on both columns:
 *
 *     left join public.lifecycle_stages s
 *       on s.stage = cl.lifecycle_stage and s.track = cl.track
 *
 * so writing a stage without a matching track makes the join find nothing,
 * `s.stale_after_days` is NULL, and coalesce falls through to the 7-day floor.
 * A partner on a 30-day cadence would start reading as overdue every week and
 * nothing anywhere would say why. Hence stagePatch(), which refuses to build
 * a patch for a pair that is not in the table, and stagesForTrack(), which
 * means the UI cannot offer one in the first place.
 */

import type { DataProvider, Identifier } from "ra-core";
import type { Invalidator } from "./touchActions";
import type {
  ContactHealth,
  ContactLifecycleRow,
  LifecycleStage,
  LifecycleTrack,
} from "./types";

/*
 * THE PRIMARY KEYS OF THESE TABLES NOW LIVE IN ./primaryKeys.ts.
 *
 * Neither `contact_lifecycle` nor `lifecycle_stages` has an `id` column, and
 * the map that says so used to be here as LIFECYCLE_PRIMARY_KEYS. It moved
 * when `company_profile` (069) joined it: that is a company-page table with
 * nothing to do with a contact's lifecycle, and a constant named for the
 * lifecycle holding it would send the next person looking in the wrong file.
 * NON_ID_PRIMARY_KEYS is still one map with one injection into upstream's
 * dataProvider.ts -- see check-injections.mjs.
 */

/**
 * The global cadence floor, mirroring the literal 7 in contact_health's
 * coalesce. Written down so the wording can say "the 7-day floor" without a
 * second, driftable copy of the number in a template string.
 */
export const CADENCE_FLOOR_DAYS = 7;

/* ── pause ─────────────────────────────────────────────────────────────── */

export type PauseState = {
  paused: boolean;
  /** YYYY-MM-DD. Present whenever a date is on file, expired or not. */
  until: string | null;
  reason: string | null;
};

/**
 * Is this contact currently snoozed, and until when?
 *
 * `paused_until` IS INCLUSIVE. Paused until the 14th means quiet THROUGH the
 * 14th, matching `contact_health`, which tests `paused_until >= current_date`
 * and adds a day before feeding the date into greatest(). Making this `>`
 * would end the pause on its own last morning: the panel would say the snooze
 * was over while the view still counted it as running, and the two screens
 * would disagree about the same person on the same day.
 *
 * COMPARED AS STRINGS, ON PURPOSE. Both sides are YYYY-MM-DD, where
 * lexicographic order is chronological order. `new Date("2026-09-14")` parses
 * as UTC midnight, which west of Greenwich is the 13th locally -- the classic
 * off-by-one that would expire every pause a day early for anyone in the US.
 *
 * `today` is injected so the boundary is testable without mocking the clock.
 *
 * Note this recomputes what `contact_health.is_paused` already says. That is
 * deliberate: the view answers with the DATABASE's current_date, and a person
 * reading the panel at 6pm on the 14th in California is still on the 14th
 * while UTC has moved on. The panel must agree with the reader's calendar.
 */
export function pauseState(
  health:
    | Pick<ContactHealth, "paused_until" | "paused_reason">
    | null
    | undefined,
  today: string,
): PauseState {
  const until = health?.paused_until ?? null;
  return {
    paused: until != null && until >= today,
    until,
    reason: health?.paused_reason ?? null,
  };
}

export type PauseCheck =
  | { ok: true; until: string }
  | { ok: false; reason: string };

/**
 * Can this date start a pause?
 *
 * AN UNDATED OR ALREADY-EXPIRED PAUSE IS NOT A PAUSE. "I'll get to it", with
 * nothing to make the silence expire, is precisely the state ~45 people sat
 * in for nine months -- so a blank date is refused rather than stored as a
 * null that quietly means forever.
 *
 * Today is allowed, and that is the same inclusive boundary as pauseState:
 * pausing until today buys the rest of today. Refusing it would make the one
 * date a person is most likely to pick fail for no reason they could see.
 */
export function validatePause(
  date: string | null | undefined,
  today: string,
): PauseCheck {
  const until = date?.trim();
  if (!until)
    return {
      ok: false,
      reason:
        "A pause needs a date. An open-ended one is how people go quiet for nine months.",
    };
  if (until < today)
    return {
      ok: false,
      reason: `${until} has already passed, so that pause would be over before it started.`,
    };
  return { ok: true, until };
}

/**
 * A YYYY-MM-DD date, in words, WITHOUT the UTC-midnight day shift.
 *
 * `paused_until` is a Postgres DATE, so it arrives as "2026-09-14" with no
 * time and no zone. `new Date("2026-09-14")` parses that as UTC midnight,
 * which is the 13th at 8pm in New York -- so the obvious formatter renders
 * every pause as ending a day early for anyone west of Greenwich. Pinning the
 * format to UTC as well as the parse keeps the two halves in the same frame
 * and prints the day that was actually stored, everywhere.
 *
 * Not shared with RelativeDate, which is for timestamps and is right to use
 * the reader's zone for those. A date is not a moment.
 */
export function formatDay(day: string, locale = "en"): string {
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${day}T00:00:00Z`));
}

/* ── cadence ───────────────────────────────────────────────────────────── */

export type CadenceSource = "record" | "stage" | "floor";

/**
 * Which of the three cadence levels actually decided the number.
 *
 * NOT COMPUTABLE FROM `contact_health`. The view coalesces the three levels
 * into `effective_stale_after` and keeps no record of which one won:
 *
 *     coalesce(cl.stale_after_days, s.stale_after_days, 7)
 *
 * and widening the view to say so is not an option -- three accountability
 * views and the Monday digest `select *` from it, which expands and freezes
 * the column list at creation time, so a new column changes all four. So the
 * two inputs are read separately and the winner is derived here, in the same
 * precedence order the SQL uses.
 *
 * Both arguments are nullable because BOTH are genuinely absent for an
 * untriaged contact: no lifecycle row at all, and therefore no stage row to
 * join to. That case is "floor", and the wording below refuses to dress it up
 * as a considered choice.
 */
export function cadenceSource(
  lifecycle: Pick<ContactLifecycleRow, "stale_after_days"> | null | undefined,
  stage: Pick<LifecycleStage, "stale_after_days"> | null | undefined,
): CadenceSource {
  return cadence(lifecycle, stage).source;
}

export type Cadence = {
  /** The number of days actually in force. */
  days: number;
  source: CadenceSource;
  /**
   * What this contact would be chased on with NO record override: the stage's
   * own default, or the floor when there is no stage. This is the number the
   * override field shows as its PLACEHOLDER, so it is always visible what an
   * override is overriding.
   */
  inherited: number;
};

/**
 * The whole cadence answer, computed once.
 *
 * ONE FUNCTION, because the alternative is two copies of a precedence rule --
 * one deciding the number and one deciding the label -- and two copies of a
 * rule is how the panel and `contact_health` come to disagree about the same
 * contact. `cadenceSource` above is a thin view onto this, not a second
 * implementation.
 *
 * The precedence mirrors the view's coalesce exactly:
 *
 *     coalesce(cl.stale_after_days, s.stale_after_days, 7)
 *
 * so `days` here and `effective_stale_after` there are the same number by
 * construction. The panel shows this one rather than the view's, because this
 * one arrives with its own explanation attached and the pair cannot drift.
 */
export function cadence(
  lifecycle: Pick<ContactLifecycleRow, "stale_after_days"> | null | undefined,
  stage: Pick<LifecycleStage, "stale_after_days"> | null | undefined,
): Cadence {
  const inherited = stage?.stale_after_days ?? CADENCE_FLOOR_DAYS;
  const own = lifecycle?.stale_after_days ?? null;
  return {
    days: own ?? inherited,
    source:
      own != null
        ? "record"
        : stage?.stale_after_days != null
          ? "stage"
          : "floor",
    inherited,
  };
}

export type CadenceCheck =
  | { ok: true; days: number | null }
  | { ok: false; reason: string };

/**
 * Can this typed value become a per-contact cadence override?
 *
 * EMPTY IS VALID AND MEANS INHERIT. That is the whole reason this returns
 * `days: null` rather than refusing: an override you cannot clear is a
 * one-way door, and the person who set 3 days during a live negotiation has
 * no way back to the stage's 30 once it is over. NULL is what
 * `contact_lifecycle.stale_after_days` holds for "no opinion", and it is what
 * makes contact_health's coalesce fall through to the stage again.
 *
 * The bounds are the column's, not taste: the CHECK constraint is
 * `stale_after_days is null or stale_after_days > 0`, and the type is
 * smallint. Zero and negatives are refused here with a sentence rather than
 * arriving as a Postgres constraint error in a toast, and 40000 is refused
 * rather than overflowing.
 */
export const CADENCE_MAX_DAYS = 32767;

export function validateCadence(raw: string | null | undefined): CadenceCheck {
  const text = (raw ?? "").trim();
  if (!text) return { ok: true, days: null };

  const days = Number(text);
  if (!Number.isInteger(days) || days < 1)
    return {
      ok: false,
      reason:
        "A cadence is a whole number of days, at least one. Leave it empty to go back to the stage default.",
    };
  if (days > CADENCE_MAX_DAYS)
    return {
      ok: false,
      reason: `${days} days is longer than the column can hold. Use a terminal stage to close a record instead of a cadence nobody will ever reach.`,
    };
  return { ok: true, days };
}

/**
 * The cadence, in words, WITH where the number came from.
 *
 * Showing "every 7 days" alone is the failure mode: the floor and a
 * deliberate weekly cadence print identically, so the panel would present
 * "nobody has triaged this person" as a considered decision. The floor case
 * says so out loud.
 */
export function describeCadence(
  days: number,
  source: CadenceSource,
  stage: { stage: string; track: LifecycleTrack } | null,
): string {
  const every = `Chase every ${days} ${days === 1 ? "day" : "days"}`;
  if (source === "record") return `${every} — this contact's own setting.`;
  if (source === "stage" && stage)
    return `${every} — the default for the ${stage.track} · ${stage.stage} stage.`;
  return `${every} — no stage set, so this falls to the ${CADENCE_FLOOR_DAYS}-day floor. Nobody has triaged this contact.`;
}

/* ── the headline ──────────────────────────────────────────────────────── */

export type Headline =
  | { kind: "terminal"; stage: string | null; track: LifecycleTrack | null }
  | { kind: "paused"; until: string; reason: string | null }
  | { kind: "overdue"; dueAt: string | null }
  | { kind: "due"; dueAt: string | null }
  | { kind: "unknown" };

/**
 * What the top line of the panel says, in precedence order.
 *
 * TERMINAL WINS, AND THIS IS NOT HYPOTHETICAL. `contact_health` computes
 * is_stale for every row including terminal ones -- a won, lost, disqualified
 * or dormant stage has no cadence, so coalesce puts it on the 7-day floor and
 * it goes stale like anything else. The three filter views drop those rows
 * with `not is_terminal`, but the raw view does not, and this panel reads the
 * raw view. Two contacts in production right now are terminal AND is_stale:
 * without this ordering the panel would tell you a closed deal is overdue.
 *
 * PAUSED BEATS OVERDUE for the same reason in reverse: due_at is a greatest()
 * that includes paused_until + 1, so a running pause makes is_stale false
 * anyway. Ordering it explicitly means the panel does not depend on that
 * arithmetic staying true.
 *
 * "unknown" is not "fine". A missing health row means the read failed or the
 * contact is not in the view, and the panel must say it does not know rather
 * than print a reassuring date it does not have.
 */
export function headline(
  health: ContactHealth | null | undefined,
  today: string,
): Headline {
  if (!health) return { kind: "unknown" };
  if (health.is_terminal)
    return {
      kind: "terminal",
      stage: health.lifecycle_stage,
      track: health.track,
    };

  const pause = pauseState(health, today);
  if (pause.paused)
    return { kind: "paused", until: pause.until!, reason: pause.reason };

  return {
    kind: health.is_stale ? "overdue" : "due",
    dueAt: health.due_at,
  };
}

/* ── stages ────────────────────────────────────────────────────────────── */

/** The tracks present in the table, in the order the table defines. */
export function tracksOf(stages: LifecycleStage[]): LifecycleTrack[] {
  const seen: LifecycleTrack[] = [];
  for (const s of stages) if (!seen.includes(s.track)) seen.push(s.track);
  return seen;
}

/**
 * The stages a track actually has, in `sort` order.
 *
 * The picker is built from this and only this, so an off-track stage is never
 * offered -- the first of the two guards against the (stage, track) trap. The
 * second is stagePatch below, because a picker is a suggestion and a patch is
 * what reaches the database.
 */
export function stagesForTrack(
  stages: LifecycleStage[],
  track: LifecycleTrack | null,
): LifecycleStage[] {
  if (!track) return [];
  return stages
    .filter((s) => s.track === track)
    .sort((a, b) => a.sort - b.sort);
}

/** The row for one (stage, track) pair, or null if the pair does not exist. */
export function findStage(
  stages: LifecycleStage[],
  stage: string | null | undefined,
  track: LifecycleTrack | null | undefined,
): LifecycleStage | null {
  if (!stage || !track) return null;
  return stages.find((s) => s.stage === stage && s.track === track) ?? null;
}

/**
 * The two columns that must move together, or nothing does.
 *
 * THROWS on a pair that is not in `lifecycle_stages`. The database would
 * refuse it too -- contact_lifecycle has a composite foreign key onto
 * (lifecycle_stage, track) -- but that refusal arrives as a Postgres error
 * message in a toast. The failure this guards is quieter and gets past the
 * constraint: writing a stage while LEAVING the track alone. ('new',
 * 'partner') and ('new', 'prospect') are both valid rows, so a partner
 * switched to `new` without its track becomes a prospect's `new`, keeps a
 * valid foreign key, and silently drops from a 30-day cadence to 7.
 *
 * So this returns BOTH columns or raises. There is no partial version.
 */
export function stagePatch(
  stage: string,
  track: LifecycleTrack,
  stages: LifecycleStage[],
): { lifecycle_stage: string; track: LifecycleTrack } {
  if (!findStage(stages, stage, track))
    throw new Error(
      `No such stage: ${track} · ${stage}. Stage and track are one key, and a pair that is not in lifecycle_stages has no cadence.`,
    );
  return { lifecycle_stage: stage, track };
}

/* ── writing ───────────────────────────────────────────────────────────── */

/**
 * The columns this panel is allowed to write.
 *
 * `last_contacted_at` is absent and must stay absent: it is DERIVED, written
 * by exactly one database trigger as max(occurred_at) over outreach
 * engagements. A second writer is how a clock starts lying, and this clock
 * lying means a person silently stops being chased.
 */
export type LifecyclePatch = Partial<
  Pick<
    ContactLifecycleRow,
    | "lifecycle_stage"
    | "track"
    | "stale_after_days"
    | "commitment_at"
    | "commitment_note"
    | "paused_until"
    | "paused_reason"
  >
>;

/**
 * The resources a lifecycle write changes, and therefore what must refetch.
 *
 * THE THREE ACCOUNTABILITY VIEWS ARE ON THIS LIST, and they are the reason it
 * is a list rather than a line. They are separate react-query keys, so
 * invalidating `contact_health` does nothing for them -- and every control in
 * this panel changes who they contain. Pausing someone removes them from
 * contacts_no_next_action; moving a contact to a terminal stage removes them
 * from all three; tightening a cadence can add them to contacts_overdue.
 *
 * Left off, the follow-ups queue would keep listing a person you just paused,
 * from cache, with no error and nothing on screen suggesting the number is
 * old. Someone would work a name that had already been dealt with -- and a
 * queue that lies about who needs chasing is the state this whole system
 * exists to end, arriving this time through a stale cache rather than a
 * stale process.
 *
 * `enrollment_steps` IS ON THIS LIST TOO, and for a reason worth stating
 * separately: since 078 that table is where an enrollment's progress LIVES --
 * `current_step` and `next_action_at` are gone -- so the panel reads it to
 * say where this contact has got to, and enroll_contact() writes it in the
 * same transaction as the enrollment row itself. Left off, Enroll would refetch
 * the enrollment and not the plan underneath it: the panel would show a brand
 * new enrollment with no steps at all, which is precisely the shape of the
 * dead row it exists to warn about. Refetching one half of a two-table write
 * is worse than refetching neither, because the screen then looks settled.
 *
 * `enrollments` IS ON THIS LIST AND `tasks` IS NOT, and the difference is who
 * writes them. Enrolling and ending are writes this panel makes, so it must
 * refetch them or the section it just changed keeps showing the old row --
 * End with a reason, and the enrollment still reads as running. Tasks are
 * written by advance_enrollments(), nightly, in the database; nothing on this
 * screen creates, ends or cancels one, so naming `tasks` here would be
 * refetching a resource that provably cannot have moved. A list that includes
 * things the panel does not write is a list nobody can reason about the next
 * time something IS missing from it.
 */
export const LIFECYCLE_INVALIDATES = [
  "contact_health",
  "contact_lifecycle",
  "contacts",
  "contacts_overdue",
  "contacts_stalled",
  "contacts_no_next_action",
  "enrollments",
  "enrollment_steps",
] as const;

/**
 * Ask react-query to refetch everything a lifecycle write changed.
 *
 * A FUNCTION RATHER THAN THREE LINES IN A CLICK HANDLER, for the reason
 * invalidateTouched was extracted: the browser test could not prove those
 * lines existed. Deleting the invalidation entirely still passed there,
 * because Playwright's clicks fire window focus events and react-query
 * refetches on focus by default -- so the panel refreshed for a reason
 * unrelated to our code, and one that does not happen to a person sitting
 * still on a contact page. That is the shape of the bug, not the fix.
 */
export function invalidateLifecycle(client: Invalidator): Promise<unknown> {
  return Promise.all(
    LIFECYCLE_INVALIDATES.map((resource) =>
      client.invalidateQueries({ queryKey: [resource] }),
    ),
  );
}

/**
 * Write the patch, inserting the row if the contact has none.
 *
 * AN UPSERT, BECAUSE ABSENCE IS THE NORMAL STATE. `contact_health` left-joins
 * `contact_lifecycle` precisely so an untriaged contact still appears, and
 * that contact is the population this system exists to catch. An update-only
 * write would fail for exactly the people whose first triage this panel is
 * for -- and it would fail on the click that was finally fixing them.
 *
 * THE EXISTENCE CHECK IS A FRESH READ, not the copy the component already
 * holds. A cached "no row" that is one write stale turns an update into a
 * second insert, and contact_id is the primary key, so that is a constraint
 * violation on someone's triage rather than a silent duplicate. Reading here
 * costs one request and makes the function correct on its own terms.
 *
 * `id` is the contact id: contact_id IS the primary key of that table, and
 * the provider is told so via NON_ID_PRIMARY_KEYS in ./primaryKeys.ts.
 */
export async function saveLifecycle(
  dataProvider: DataProvider,
  contactId: Identifier,
  patch: LifecyclePatch,
): Promise<void> {
  const { data } = await dataProvider.getList<any>("contact_lifecycle", {
    filter: { contact_id: contactId },
    pagination: { page: 1, perPage: 1 },
    sort: { field: "contact_id", order: "ASC" },
  });
  const existing = data[0];

  if (existing) {
    await dataProvider.update("contact_lifecycle", {
      id: contactId,
      data: patch,
      previousData: existing,
    });
    return;
  }

  await dataProvider.create("contact_lifecycle", {
    data: { contact_id: contactId, ...patch },
  });
}
